import type { ColumnVisibilityState, OnChangeFn } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { UserUiSettings } from '~/shared/data-contract/user-ui-settings';

import { useUserSettingsUiMutation } from './use-user-settings-ui';

export type ColumnVisibilitySettingKey = Extract<keyof UserUiSettings, `${string}ColumnVisibility`>;

const EMPTY_COLUMN_VISIBILITY: ColumnVisibilityState = {};
const NO_COLUMN_IDS: readonly string[] = [];

export const useColumnVisibilitySetting = (
    key: ColumnVisibilitySettingKey,
    options?: {
        readonly defaultHidden?: readonly string[];
        readonly forcedVisible?: readonly string[];
    },
): {
    readonly columnVisibility: ColumnVisibilityState;
    readonly onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState>;
} => {
    const { defaultHidden = NO_COLUMN_IDS, forcedVisible = NO_COLUMN_IDS } = options ?? {};
    const { applicationState, validatingCobrainerApiFetcher } = useApplicationContext();
    const storedVisibility = applicationState.uiSettings.get(key).unwrapOr(EMPTY_COLUMN_VISIBILITY);
    const { userSettingsUiMutationOptimistic } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);
    const { mutate } = userSettingsUiMutationOptimistic;
    const columnVisibility = useMemo<ColumnVisibilityState>(
        () => ({
            ...Object.fromEntries(defaultHidden.map((columnId) => [columnId, false])),
            ...storedVisibility,
            ...Object.fromEntries(forcedVisible.map((columnId) => [columnId, true])),
        }),
        [defaultHidden, forcedVisible, storedVisibility],
    );
    const onColumnVisibilityChange = useCallback<OnChangeFn<ColumnVisibilityState>>(
        (updater) => {
            const nextVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
            mutate({
                [key]: {
                    ...nextVisibility,
                    ...Object.fromEntries(forcedVisible.map((columnId) => [columnId, true])),
                },
            });
        },
        [columnVisibility, forcedVisible, key, mutate],
    );

    return { columnVisibility, onColumnVisibilityChange };
};
